import React, { useEffect, useState } from "react";
import { ric } from "@/lib/ric";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { ClipboardCheck, Loader2 } from "lucide-react";

// Simplified count session UI: supervisor opens a session (auto lists all bins);
// staff records a per-bin walk with per-item counted_kg or a hag_owned skip
// marker. Submit checks every bin was visited or explicitly skipped, then writes
// approved count adjustments to the ledger.
export default function RICCount() {
  const { user } = useAuth();
  const { lang } = useLang();
  const t = (en, hi) => (lang === "en" ? en : hi);
  const isSup = user?.role === "ric_supervisor";
  const [items, setItems] = useState([]);
  const [session, setSession] = useState(null);
  const [openBin, setOpenBin] = useState("");
  const [walk, setWalk] = useState({}); // yarn_item_id -> {counted_kg, hag_owned}
  const [skipReason, setSkipReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const loadLatest = async () => {
    const all = await ric.counts();
    const open = all.find((c) => c.status === "Open");
    setSession(open || (all[0] || null));
    setItems(await ric.yarnItems());
  };
  useEffect(() => { loadLatest(); }, []);

  const openSession = async () => {
    setBusy(true);
    try { await ric.openCount({ counters: [user?.id] }); await loadLatest(); }
    catch (e) { setMsg(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const openWalk = (b) => {
    setOpenBin(b.bin_id);
    setWalk({});
    setSkipReason("");
    setMsg("");
  };

  const saveWalk = async () => {
    setBusy(true);
    try {
      const lines = Object.entries(walk).map(([yid, v]) => ({
        yarn_item_id: yid,
        counted_kg: v.hag_owned ? undefined : parseFloat(v.counted_kg || 0),
        hag_owned: !!v.hag_owned,
      }));
      await ric.walk(session.id, {
        bin_id: openBin,
        lines,
        skipped_reason: skipReason || null,
      });
      setMsg(t("Walk saved", "गिनती सहेजी"));
      await loadLatest();
      setOpenBin("");
    } catch (e) { setMsg(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await ric.submitCount(session.id);
      setMsg(t(`Submitted. ${r.adjustments} adjustments recorded.`, `जमा। ${r.adjustments} समायोजन दर्ज।`));
      await loadLatest();
    } catch (e) { setMsg(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  if (!session) {
    return (
      <div data-testid="ric-count-empty">
        <h1 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: "var(--brand-navy)", fontFamily: "'Fraunces', serif" }}>
          <ClipboardCheck size={24} /> {t("Physical count", "फिज़िकल गिनती")}
        </h1>
        {isSup && (
          <button data-testid="ric-count-open" onClick={openSession} disabled={busy}
            className="h-11 px-4 rounded-md text-sm font-semibold text-white"
            style={{ background: "var(--brand-navy)" }}>
            {t("Open new count session", "नया सत्र खोलें")}
          </button>
        )}
        {!isSup && <div className="text-sm text-[#7C8E9E]">{t("Waiting for supervisor to open a session.", "सुपरवाइज़र सत्र खोलेंगे।")}</div>}
      </div>
    );
  }

  return (
    <div data-testid="ric-count">
      <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: "var(--brand-navy)", fontFamily: "'Fraunces', serif" }}>
            <ClipboardCheck size={24} /> {t("Physical count", "फिज़िकल गिनती")}
          </h1>
          <div className="text-xs text-[#7C8E9E]">
            Session #{session.id.slice(0, 6)} · <b>{session.status}</b> ·
            {" "}{session.bin_walks.filter((b) => b.status !== "pending").length}/{session.bin_walks.length} {t("bins visited", "बिन जाँचे")}
          </div>
        </div>
        {isSup && session.status === "Open" && (
          <button data-testid="ric-count-submit" onClick={submit} disabled={busy}
            className="h-10 px-4 rounded-md text-sm font-semibold text-white"
            style={{ background: "var(--brand-navy)" }}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : null} {t("Submit session", "सत्र जमा करें")}
          </button>
        )}
      </div>

      {msg && <div className="mb-3 text-xs bg-amber-50 border border-amber-200 rounded p-2" data-testid="ric-count-msg">{msg}</div>}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {session.bin_walks.map((b) => (
          <button key={b.bin_id} data-testid={`ric-count-bin-${b.bin_id}`}
            onClick={() => openWalk(b)}
            className={`p-2 rounded-md border text-left ${
              b.status === "pending" ? "border-[#1B3A5C]/15 bg-white"
              : b.status === "skipped" ? "border-amber-300 bg-amber-50"
              : "border-green-300 bg-green-50"
            }`}>
            <div className="text-xs font-bold">{b.bin_number}</div>
            <div className="text-[10px] text-[#4A627A]">{b.status}</div>
          </button>
        ))}
      </div>

      {openBin && (
        <div className="hag-surface p-4 mt-4" data-testid="ric-count-walk">
          <div className="text-sm font-bold mb-2">
            {t("Bin walk", "बिन गिनती")} — {session.bin_walks.find((b) => b.bin_id === openBin)?.bin_number}
          </div>
          <div className="mb-2">
            <input data-testid="ric-count-skip" value={skipReason} onChange={(e) => setSkipReason(e.target.value)}
              placeholder={t("Skip reason (leave blank to count)", "छोड़ने का कारण (गिनने के लिए खाली)")}
              className="w-full h-10 px-2 border border-[#1B3A5C]/15 rounded-md text-sm" />
          </div>
          {!skipReason && (
            <div className="space-y-1 max-h-72 overflow-y-auto">
              {items.filter((i) => i.default_bin_id === openBin).map((i) => (
                <div key={i.id} className="flex items-center gap-2" data-testid={`ric-count-item-${i.id}`}>
                  <div className="flex-1 text-xs">{i.yarn_type} · {i.yarn_count} · {i.colour || "—"}</div>
                  <label className="flex items-center gap-1 text-xs">
                    <input type="checkbox" data-testid={`ric-count-hag-${i.id}`} checked={!!walk[i.id]?.hag_owned}
                      onChange={(e) => setWalk({ ...walk, [i.id]: { ...walk[i.id], hag_owned: e.target.checked } })} />
                    {t("HAG-owned", "HAG का")}
                  </label>
                  {!walk[i.id]?.hag_owned && (
                    <input data-testid={`ric-count-kg-${i.id}`} type="number" step="0.1" min="0"
                      value={walk[i.id]?.counted_kg || ""}
                      onChange={(e) => setWalk({ ...walk, [i.id]: { ...walk[i.id], counted_kg: e.target.value } })}
                      className="w-20 h-8 px-1 border border-[#1B3A5C]/15 rounded text-xs text-right" placeholder="kg" />
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="mt-3 flex gap-2">
            <button data-testid="ric-count-save" onClick={saveWalk} disabled={busy}
              className="h-10 px-4 rounded-md text-sm font-semibold text-white"
              style={{ background: "var(--brand-navy)" }}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : null} {t("Save walk", "सहेजें")}
            </button>
            <button onClick={() => setOpenBin("")} className="h-10 px-3 rounded-md border border-[#1B3A5C]/20 text-sm">
              {t("Cancel", "रद्द")}
            </button>
          </div>
        </div>
      )}

      {session.status === "Submitted" && (
        <div className="hag-surface p-4 mt-4" data-testid="ric-count-adjustments">
          <div className="text-sm font-semibold mb-2" style={{ color: "var(--brand-navy)" }}>
            {t("Approved adjustments", "अनुमोदित समायोजन")}
          </div>
          {(session.adjustments || []).map((a, i) => (
            <div key={i} className="text-xs py-1 border-b border-[#1B3A5C]/5">
              Bin {a.bin_id?.slice(0, 6)} · Item {a.yarn_item_id?.slice(0, 6)} · book <b>{a.book}</b> vs counted <b>{a.counted}</b> · Δ {a.delta > 0 ? "+" : ""}{a.delta}
            </div>
          ))}
          {(!session.adjustments || session.adjustments.length === 0) && (
            <div className="text-xs text-[#7C8E9E]">{t("Book matched counted perfectly.", "किताबी और गिना समान।")}</div>
          )}
        </div>
      )}
    </div>
  );
}
